import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { FeatureToggle } from '@sde/widgets-core/src/common/FeatureToggle';
import { event } from '@uhg-abyss/web/tools/event';
import { NavMenu } from '@uhg-abyss/web/ui/NavMenu';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { styled } from '@uhg-abyss/web/tools/styled';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { useModal } from '@src/state/modal';
import { useCoreStore } from '@src/store/useCoreStore';
import { ReactivateTrainingTips } from './ReactivateTrainingTips';
import { AccountNumber } from './AccountNumber';

const StyledCell = styled(NavMenu.Item, {
  '& .abyss-nav-menu-link': {
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '4px 8px',
    '&:hover': {
      backgroundColor: '$gray2',
    },
  },
});

const NavMenuItem = ({ datatestid, title, href, onClick }) => {
  return (
    <div data-testId={datatestid} data-cy={datatestid}>
      <StyledCell title={title} href={href} onClick={onClick} />
    </div>
  );
};

const HorizontalRule = styled('div', {
  margin: '$sm 0',
  height: 0,
  width: '100%',
  borderTop: '1px solid $gray3',
  marginBottom: '16px !important',
});

const StyledNavMenuColumn = styled(NavMenu.Column, {
  '&.abyss-nav-menu-column-title': {
    color: '$gray8',
  },
});

const StyledNavMenuItem = styled(NavMenu.Item, {
  '&.abyss-nav-menu-link': {
    height: 'inherit',
    minHeight: 'inherit',
    alignItems: 'flex-start !important',
    padding: '4px 8px !important',
  },
});

const StyledBottomNavMenuItem = styled(NavMenu.Item, {
  '&.abyss-nav-menu-link-root': {
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },
  '&.abyss-nav-menu-link': {
    height: 'inherit',
    minHeight: 'inherit',
    alignItems: 'flex-start !important',
    paddingLeft: '0px !important',
    paddingRight: '0px !important',
  },
  '&.abyss-icon-symbol': {
    height: '24px !important',
    width: '24px !important',
    fontSize: '24px !important',
  },
  '&.abyss-nav-menu-item-link-icon': {
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px !important',
  },
});

const StyledNavMenuColumns = styled(NavMenu.Columns, {
  '&.abyss-nav-menu-columns-root': {
    padding: '$sm $md',
  },
});

const StyledReactivateTrainingTipsWrapper = styled('div', {
  '#reactivate-training-tips': {
    padding: '4px 0px !important',
  },
  height: '40px !important',
});

const StyledRequestAccessWrapper = styled(StyledBottomNavMenuItem, {
  display: 'flex',
  flexDirection: 'row !important',
});

const dsCodeArray = ['DSA:V:USR:NU', 'DSA:V:USR:IR', 'DSA:V:USR:PR'];

export const MyAccount = () => {
  const { user } = useUser.state();
  const { provider } = user;
  const { code } = user;
  const navigate = useNavigate();
  const { toggleModal } = useModal.actions();
  const { accountNumber } = useCoreStore();

  const tailoredTrainingPermission = Object.keys(provider).length !== 0;

  const handleSignout = (linkText) => {
    storage.session.remove('selectedTab');
    const permissionsStorageKey = 'provider-permissions';
    storage.local.remove(permissionsStorageKey);
    storage.session.remove('session-expiry-time');
    event('PROFILE_SETTING_LINKS', { linkText });
    window.location.href = `${
      getFeature('OHID_SESSION_REWORK')
        ? `${config('IDENTITY_API_URL_BREAKOUT')}/logout/v2`
        : `${config('IDENTITY_API_URL_BREAKOUT')}/logout`
    }`;
  };

  const handleEditMode = () => {
    storage.session.set('accountInformationEditMode', 'off');
    storage.session.set('communicationPreferenceEditMode', 'off');
    storage.session.set('trainingPreferenceEditMode', 'off');
    storage.session.set('priorAuthAppealEditMode', 'off');
    storage.session.set('claimAppealEditMode', 'off');
    storage.session.set('pendedTicketEditMode', 'off');
    storage.session.set('pendingClaimsEditMode', 'off');
    storage.session.set('reconsiderationEditMode', 'off');
    storage.session.set('smartEditsEditMode', 'off');
    storage.session.set('priorAuthEditMode', 'off');
    storage.session.set('refferalEditMode', 'off');
    storage.session.set('admissionEditMode', 'off');
    storage.session.set('dischargeEditMode', 'off');
    storage.session.set('emergencyEditMode', 'off');
    storage.session.set('ppcTrackitEditMode', 'off');
    storage.session.set('requiredCommunicationPreferenceEdits', 'off');
  };
  const handleExitLink = (linkText, href) => {
    if (linkText === 'Organization and Tax ID Access') {
      event('EXIT_LINK_NAVIGATE', { linkText });
    } else {
      event('PROFILE_SETTING_LINKS', { linkText });
    }

    handleEditMode();
    if (href) {
      navigate(href);
    }
  };

  const columnTitle = 'Profile & Settings';
  const openPermissionsModal = useCallback((toolName) => {
    toggleModal({
      key: 'permissions',
      isOpen: true,
      toolName,
    });
  }, []);

  return (
    <React.Fragment>
      <If condition={accountNumber.show}>
        <AccountNumber
          accountNumberValue={accountNumber.value}
          accountNumberApiError={accountNumber.error}
        />
      </If>
      <StyledNavMenuColumns>
        <StyledNavMenuColumn data-testid="profile-title-id" title={columnTitle}>
          <FeatureToggle toggle="PREFERENCE_CENTER">
            <StyledNavMenuItem
              data-testid="top-bar-pref-center-link"
              title="Account Information"
              // href="/prefcenter"
              onClick={() =>
                handleExitLink('Account Information', '/prefcenter')
              }
            />
            <FeatureToggle toggle="ORG_TAXID_ACCESS_NAV_ITEM">
              <StyledNavMenuItem
                data-testid="top-bar-pref-center-org-tax-link"
                title="Organization & Tax ID Access"
                // href="/prefcenter/orgtaxIdAccess"
                onClick={() =>
                  handleExitLink(
                    'Organization & Tax ID Access',
                    '/prefcenter/orgtaxIdAccess'
                  )
                }
              />
            </FeatureToggle>
            <FeatureToggle toggle="COMM_PREF_TOGGLE">
              <StyledNavMenuItem
                datatestid="top-bar-pref-center-communication-pref-link"
                title="Communication Preferences"
                // href="/prefcenter/comm-pref"
                onClick={() =>
                  handleExitLink(
                    'Communication Preferences',
                    '/prefcenter/comm-pref'
                  )
                }
              />
            </FeatureToggle>
            <FeatureToggle toggle="TRAINING_LIBRARY_TOGGLE">
              <StyledNavMenuItem
                datatestid="top-bar-pref-center-training-library-link"
                title="Tailored Training"
                href={
                  tailoredTrainingPermission
                    ? '/prefcenter/tailoredtraining'
                    : ''
                }
                onClick={
                  tailoredTrainingPermission
                    ? () => handleExitLink('Tailored Training')
                    : () => openPermissionsModal('Tailored Training')
                }
              />
            </FeatureToggle>
            <FeatureToggle toggle="PREF_CENTER_TRAININGS">
              <StyledNavMenuItem
                datatestid="top-bar-pref-center-trainings-link"
                title="Training Preferences"
                href="/prefcenter/trainings"
                onClick={() => handleExitLink('Training')}
              />
            </FeatureToggle>
          </FeatureToggle>
          <HorizontalRule />
          <FeatureToggle toggle="REQUEST_ACCESS_DS">
            <StyledRequestAccessWrapper
              datatestid="top-bar-request-access-link"
              title="Request Access"
              href={`${config('DIGITAL_SECURITY_URL')}`}
              onClick={() => handleExitLink('Request Access')}
            />
          </FeatureToggle>
          <If condition={!dsCodeArray.includes(code)}>
            <StyledReactivateTrainingTipsWrapper>
              <ReactivateTrainingTips />
            </StyledReactivateTrainingTipsWrapper>
          </If>
          <StyledBottomNavMenuItem
            datatestid="top-bar-sign-out-link"
            title="Sign Out"
            href={``}
            onClick={() => handleSignout('Sign Out')}
          />
        </StyledNavMenuColumn>
      </StyledNavMenuColumns>
    </React.Fragment>
  );
};

NavMenuItem.propTypes = {
  datatestid: PropTypes.string,
  title: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
};

NavMenuItem.defaultProps = {
  datatestid: null,
  title: null,
  href: '',
  onClick: () => {},
};
